import * as Logger from '@navigator/frontend-logger';

/*
 * This service is a workaround to fix a mailer bug. Customers see the mailer start page
 * when they switch to the mailer app, which should never happen. The 3C-Team think
 * the reason is a race-conditions, so that the the IAC call is not detected
 * by the mailer [POS-2087].
 */
export class FocusWorkaroundService {

  private applied = false;
  private sendMessageIntervalId = 0;
  private stopPollingTimerId = 0;
  private onMessageWrapped: (evt: MessageEvent) => void;
  private window: Window;
  private document: Document;

  constructor() {
    this.window = window;
    this.document = document;

    // wrap onMessage to remove the correct reference on cleanup
    this.onMessageWrapped = (evt) => this.onMessage(evt);
  }

  static get POLL_INTERVAL(): number {
    return 500; // ms
  }

  static get STOP_POLLING_TIMER(): number {
    return 20000; // ms
  }

  // send by the start page, when MESSAGE was received
  static get RESPONSE_SUCCESS(): string {
    return 'receivedEvent';
  }

  // send from all other pages, to stop fallback.
  // This is important when the customer reloads the browser,
  // because then the start page will not be loaded by the mailer.
  static get RESPONSE_STOP(): string {
    return 'stopPostMessageFallback';
  }

  static get MESSAGE(): string {
    return 'postMessageFallback';
  }

  static get I_FRAME_ID(): string {
    return 'thirdPartyFrame_mail';
  }

  apply(): void {
    // apply focus workaround only for the first focus
    if (this.applied) {
      return;
    }

    this.applied = true;

    this.window.addEventListener('message', this.onMessageWrapped);
    this.sendMessageIntervalId = this.window.setInterval(() => this.sendMessage(),
      FocusWorkaroundService.POLL_INTERVAL);
    this.stopPollingTimerId = this.window.setTimeout(() => this.stopPolling(),
      FocusWorkaroundService.STOP_POLLING_TIMER);
  }

  private onMessage(evt: MessageEvent) {
    // only listen to the mailer iframe
    if (evt.source !== this.getContentWindow()) {
      return;
    }

    if (evt.data === FocusWorkaroundService.RESPONSE_SUCCESS ||
      evt.data === FocusWorkaroundService.RESPONSE_STOP
    ) {
      Logger.info(`FocusWorkaround: Received response ('${evt.data}')`);
      this.cleanup();
    }
  }

  private sendMessage() {
    const contentWindow = this.getContentWindow();

    if (contentWindow) {
      contentWindow.postMessage(FocusWorkaroundService.MESSAGE, '*');
    }
  }

  private stopPolling() {
    Logger.error('FocusWorkaround: Got no response from mail application');
    this.cleanup();
  }

  private getContentWindow(): Window {
    try {
      return (this.document.getElementById(FocusWorkaroundService.I_FRAME_ID) as HTMLIFrameElement).contentWindow;
    } catch (ex) {
      // Logger.error('FocusWorkaround: Could not get window from mail application');
    }

    return undefined;
  }

  private cleanup() {
    this.window.clearInterval(this.sendMessageIntervalId);
    this.window.clearTimeout(this.stopPollingTimerId);
    this.window.removeEventListener('message', this.onMessageWrapped);
  }

}

export const focusWorkaroundService = new FocusWorkaroundService();
